import { Environment } from '~/engine/data/Environment';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import {
    SmoothBrushAction,
    SmoothBrushActionState,
} from '../../actions/contouring-actions/SmoothBrushAction';
import { StrokeMode } from '~/state/atoms/toolsAtoms';
import { CircleCursorStrokeTool } from './CircleCursorStrokeTool';

export const MIN_SMOOTH_STRENGTH = 1;
export const MAX_SMOOTH_STRENGTH = 5;

/**
 * {@link SmoothBrushTool} locally smooths an existing ROI's SDF under
 * the cursor instead of the whole structure. Each pass applies a
 * brush-bounded Gaussian blur (progressive while dragging); in 2D mode
 * only the brushed view's current slice changes, in 3D mode a
 * short-range volumetric neighbourhood does.
 *
 * Delegates stroke state management to {@link SmoothBrushAction} /
 * {@link SmoothBrushActionState}, and the cursor and stroke interaction
 * — including the diameter range and Shift+Wheel sizing ladder it shares
 * with the deform tool — to {@link CircleCursorStrokeTool}.
 */
export class SmoothBrushTool extends CircleCursorStrokeTool<
    SmoothBrushActionState,
    SmoothBrushAction
> {
    public readonly toolType = 'smoothBrush';

    private readonly _strength: number;
    private readonly _mode: StrokeMode;

    public constructor(
        structure: RegionOfInterest,
        environment: Environment | undefined,
        diameter: number,
        strength: number,
        mode: StrokeMode,
        diameterChangedCallback: (diameter: number) => void
    ) {
        super(structure, environment, diameter, diameterChangedCallback);
        this._strength = clampStrength(strength);
        this._mode = mode;
    }

    public get strength(): number {
        return this._strength;
    }

    public get mode(): StrokeMode {
        return this._mode;
    }

    protected override isStrokeConfinedToPlane(): boolean {
        return this._mode === '2d';
    }

    protected override createAction(): SmoothBrushAction {
        return new SmoothBrushAction(
            this.structure,
            this.diameter,
            this._strength,
            this._mode
        );
    }
}

function clampStrength(strength: number): number {
    return Math.max(
        MIN_SMOOTH_STRENGTH,
        Math.min(MAX_SMOOTH_STRENGTH, strength)
    );
}
